import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Toast from "../../components/Toast/toast.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import BoardCard from "../../components/Order/BoardCard.js";
import BoardOrderDetailsModal from "../../components/Order/BoardOrderDetailsModal.js";
import { useAuth } from "../../utils/authContext.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { useOrderBoard } from "../../hooks/useOrderBoard.js";
import {
    BOARD_COLUMNS,
    BOARD_SCOPE,
    NO_COMPANY_KEY,
    companyKeyOf,
    filterBoardOrdersByTime,
    groupBoardLanes,
    uniqueBoardTimes
} from "../../utils/orderBoard.js";

const BOARD_SCOPE_STORAGE_KEY = "bychef_kms_order_board_scope";

const readScope = () => {
    try {
        return sessionStorage.getItem(BOARD_SCOPE_STORAGE_KEY) === BOARD_SCOPE.ALL ? BOARD_SCOPE.ALL : BOARD_SCOPE.TODAY;
    } catch {
        return BOARD_SCOPE.TODAY;
    }
};

const GRID_STYLE = { gridTemplateColumns: `repeat(${BOARD_COLUMNS.length}, minmax(220px, 1fr))` };

const OrderBoard = () => {
    const navigate = useNavigate();
    const { chefData } = useAuth();
    const { language } = useLanguage();
    const toastRef = useRef(null);

    const [scope, setScope] = useState(readScope);
    const [timeFilter, setTimeFilter] = useState(null);
    const [collapsedLanes, setCollapsedLanes] = useState(() => new Set());
    const [dragging, setDragging] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const { orders, newIds, pendingIds, undoStartedAt, loading, error, reload, moveOrder, revertDelivered, dismissNew } =
        useOrderBoard({
            chefId: chefData?.id,
            scope,
            toastRef
        });

    const statusLabel = useCallback(
        (status) => getTranslation(`orderStatuses.${status.toLowerCase()}`, language),
        [language]
    );

    const boardOrders = useMemo(() => Object.values(orders), [orders]);
    const boardTimes = useMemo(() => uniqueBoardTimes(boardOrders), [boardOrders]);
    const visibleOrders = useMemo(
        () => filterBoardOrdersByTime(boardOrders, timeFilter && boardTimes.includes(timeFilter) ? timeFilter : null),
        [boardOrders, boardTimes, timeFilter]
    );
    const lanes = useMemo(() => groupBoardLanes(visibleOrders), [visibleOrders]);

    const columnCounts = useMemo(() => {
        const counts = Object.fromEntries(BOARD_COLUMNS.map((s) => [s, 0]));
        for (const lane of lanes) {
            for (const status of BOARD_COLUMNS) counts[status] += lane.cells[status].length;
        }
        return counts;
    }, [lanes]);

    const activeTotal = lanes.reduce((sum, lane) => sum + lane.activeCount, 0);

    const handleScopeChange = (next) => {
        setScope(next);
        try {
            sessionStorage.setItem(BOARD_SCOPE_STORAGE_KEY, next);
        } catch {
            // Storage unavailable: the choice just won't be remembered.
        }
    };

    const toggleLane = (key) => {
        setCollapsedLanes((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    /** Card click: dismiss the "new" flag and open the quick-look modal. */
    const openOrder = useCallback(
        (order) => {
            dismissNew(order.id);
            setSelectedOrder(order);
        },
        [dismissNew]
    );

    /** "View full order" from the modal: keep the old direct navigation. */
    const openFullOrder = useCallback(
        (order) => {
            dismissNew(order.id);
            setSelectedOrder(null);
            const state = { fromPage: "board" };
            const src = order?.source;
            const externalKey = String(order?.orderNumber ?? order?.number ?? "").trim();
            if (order?.id == null && src != null && src !== "BYCHEF" && externalKey !== "") {
                const qs = new URLSearchParams({ source: String(src) }).toString();
                navigate(`/orders/external/${encodeURIComponent(externalKey)}?${qs}`, {
                    state: { ...state, source: String(src) }
                });
                return;
            }
            navigate(`/orders/${order.id}`, { state });
        },
        [dismissNew, navigate]
    );

    const handleDrop = (e, laneKey, status) => {
        e.preventDefault();
        const drag = dragging;
        setDragging(null);
        setDropTarget(null);
        if (!drag || drag.laneKey !== laneKey) return;
        moveOrder(drag.id, status);
    };

    const activeTime = timeFilter && boardTimes.includes(timeFilter) ? timeFilter : null;

    const chipButton = (key, pressed, onClick, children, className) => (
        <button
            key={key}
            type="button"
            onClick={onClick}
            aria-pressed={pressed}
            className={className}
        >
            {children}
        </button>
    );

    const segmentClass = (pressed) =>
        `px-3 py-1.5 text-xs font-semibold transition-colors ${
            pressed
                ? "bg-gray-900 text-white dark:bg-[#30363d] dark:text-[#e6edf3]"
                : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-[#161b22] dark:text-[#c9d1d9] dark:hover:bg-[#21262d]"
        }`;

    const timeChipClass = (pressed) =>
        `px-2 py-1 rounded-md text-xs font-semibold tabular-nums border transition-colors ${
            pressed
                ? "bg-gray-900 text-white border-gray-900 dark:bg-[#30363d] dark:text-[#e6edf3] dark:border-[#58a6ff]/50"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-[#161b22] dark:text-[#c9d1d9] dark:border-[#30363d] dark:hover:bg-[#21262d]"
        }`;

    const timeChip = (key, pressed, onClick, children) =>
        chipButton(key, pressed, onClick, children, timeChipClass(pressed));

    const scopeButton = (value, labelKey) =>
        chipButton(value, scope === value, () => handleScopeChange(value), getTranslation(labelKey, language), segmentClass(scope === value));

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-[#e6edf3]">
                        {getTranslation("orderBoardPage.title", language)}
                    </h1>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-[#8b949e]">
                        {getTranslation("orderBoardPage.deliveredHint", language)}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {!loading && !error && (
                        <span className="text-xs tabular-nums text-gray-600 dark:text-[#8b949e]">
                            {getTranslation("orderBoardPage.activeOrders", language)}: <b className="text-gray-900 dark:text-[#e6edf3]">{activeTotal}</b>
                        </span>
                    )}
                    {!loading && !error && boardTimes.length > 0 && (
                        <div className="flex min-w-0 max-w-full flex-col gap-1 sm:flex-row sm:items-center">
                            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-[#8b949e]">
                                {getTranslation("orderBoardPage.timeFilter", language)}
                            </span>
                            <div
                                role="group"
                                aria-label={getTranslation("orderBoardPage.timeFilter", language)}
                                className="flex max-w-full flex-wrap gap-1"
                            >
                                {timeChip("time-all", !activeTime, () => setTimeFilter(null), getTranslation("orderBoardPage.all", language))}
                                {boardTimes.map((time) =>
                                    timeChip(time, activeTime === time, () => setTimeFilter((prev) => (prev === time ? null : time)), time)
                                )}
                            </div>
                        </div>
                    )}
                    <div
                        role="group"
                        aria-label={getTranslation("orderBoardPage.scope", language)}
                        className="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-[#30363d]"
                    >
                        {scopeButton(BOARD_SCOPE.TODAY, "orderBoardPage.today")}
                        {scopeButton(BOARD_SCOPE.ALL, "orderBoardPage.all")}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex h-64 items-center justify-center">
                    <LoadingSpinner />
                </div>
            ) : error ? (
                <ErrorDisplay error={error} onRetry={reload} />
            ) : (
                <div className="max-h-[calc(100vh-230px)] overflow-auto rounded-lg border border-gray-200 bg-gray-50 dark:border-[#30363d] dark:bg-[#0d1117]">
                    <div className="grid min-w-max" style={GRID_STYLE}>
                        {BOARD_COLUMNS.map((status, idx) => (
                            <div
                                key={status}
                                className={`sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-100 px-3 py-2 dark:border-[#30363d] dark:bg-[#161b22] ${
                                    idx < BOARD_COLUMNS.length - 1 ? "border-r" : ""
                                }`}
                            >
                                <span className="truncate text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-[#c9d1d9]">
                                    {statusLabel(status)}
                                </span>
                                <span className="rounded-full bg-gray-200 px-2 py-px text-xs font-semibold tabular-nums text-gray-700 dark:bg-[#30363d] dark:text-[#c9d1d9]">
                                    {columnCounts[status]}
                                </span>
                            </div>
                        ))}

                        {lanes.length === 0 && (
                            <div
                                className="px-4 py-16 text-center text-sm text-gray-500 dark:text-[#8b949e]"
                                style={{ gridColumn: "1 / -1" }}
                            >
                                {getTranslation(
                                    activeTime
                                        ? "orderBoardPage.emptyTime"
                                        : scope === BOARD_SCOPE.TODAY
                                          ? "orderBoardPage.emptyToday"
                                          : "orderBoardPage.empty",
                                    language
                                )}
                            </div>
                        )}

                        {lanes.map((lane) => {
                            const collapsed = collapsedLanes.has(lane.key);
                            const laneName =
                                lane.key === NO_COMPANY_KEY
                                    ? getTranslation("orderPage.noCompany", language)
                                    : lane.companyName;
                            return (
                                <React.Fragment key={lane.key}>
                                    <div
                                        className="border-b border-gray-200 bg-white dark:border-[#30363d] dark:bg-[#0d1117]"
                                        style={{ gridColumn: "1 / -1" }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleLane(lane.key)}
                                            aria-expanded={!collapsed}
                                            className="sticky left-0 flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:text-blue-700 dark:text-[#e6edf3] dark:hover:text-[#58a6ff]"
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={`inline-block text-xs text-gray-500 transition-transform dark:text-[#8b949e] ${collapsed ? "-rotate-90" : ""}`}
                                            >
                                                ▾
                                            </span>
                                            <span>{laneName}</span>
                                            <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-[#8b949e]">
                                                {lane.activeCount} {getTranslation("orderBoardPage.ordersShort", language)}
                                            </span>
                                        </button>
                                    </div>

                                    {!collapsed &&
                                        BOARD_COLUMNS.map((status, idx) => {
                                            const cellKey = `${lane.key}|${status}`;
                                            const canDrop = dragging?.laneKey === lane.key && dragging?.status !== status;
                                            const isTarget = canDrop && dropTarget === cellKey;
                                            return (
                                                <div
                                                    key={cellKey}
                                                    onDragOver={(e) => {
                                                        if (!canDrop) return;
                                                        e.preventDefault();
                                                        if (dropTarget !== cellKey) setDropTarget(cellKey);
                                                    }}
                                                    onDragLeave={() => {
                                                        if (dropTarget === cellKey) setDropTarget(null);
                                                    }}
                                                    onDrop={(e) => handleDrop(e, lane.key, status)}
                                                    className={`flex min-h-[72px] flex-col gap-2 border-b border-gray-200 p-2 dark:border-[#30363d] ${
                                                        idx < BOARD_COLUMNS.length - 1 ? "border-r" : ""
                                                    } ${
                                                        isTarget
                                                            ? "bg-blue-50 outline outline-2 -outline-offset-2 outline-blue-400 dark:bg-[#0d1d33] dark:outline-[#388bfd]"
                                                            : canDrop
                                                              ? "bg-gray-100/70 dark:bg-[#161b22]/60"
                                                              : ""
                                                    }`}
                                                >
                                                    {lane.cells[status].map((order) => {
                                                        const id = String(order.id);
                                                        const colIdx = BOARD_COLUMNS.indexOf(order.status);
                                                        const nextStatus = BOARD_COLUMNS[colIdx + 1];
                                                        return (
                                                            <BoardCard
                                                                key={id}
                                                                order={order}
                                                                isNew={newIds.has(id)}
                                                                isPending={pendingIds.has(id)}
                                                                undoStartedAt={undoStartedAt[id]}
                                                                showDate={scope === BOARD_SCOPE.ALL}
                                                                nextStatusLabel={nextStatus ? statusLabel(nextStatus) : null}
                                                                onOpen={() => openOrder(order)}
                                                                onMoveNext={() => nextStatus && moveOrder(order.id, nextStatus)}
                                                                onRevert={() => revertDelivered(order.id)}
                                                                onDragStart={(e) => {
                                                                    e.dataTransfer.effectAllowed = "move";
                                                                    e.dataTransfer.setData("text/plain", id);
                                                                    setDragging({ id, laneKey: companyKeyOf(order), status: order.status });
                                                                }}
                                                                onDragEnd={() => {
                                                                    setDragging(null);
                                                                    setDropTarget(null);
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}

            {selectedOrder && (
                <BoardOrderDetailsModal
                    order={orders[String(selectedOrder.id)] ?? selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                    onOpenFull={openFullOrder}
                    toastRef={toastRef}
                />
            )}

            <Toast ref={toastRef} />
        </div>
    );
};

export default OrderBoard;
